import { FC, useEffect } from 'react';
import { useSSE } from 'hooks/useSSE';
import { API_BASE_URL, API_VERSION } from 'constants/common';
import { getNotifications, selectCurrentTeam, selectUserDetails, showNotifier } from 'containers/app/appSlice';
import { JobSSEEvents } from 'containers/jobs/constant';
import { VoidFn } from 'types/baseTypes';
import { useAppDispatch, useAppSelector } from 'hooks';
import { NotifierTypes } from 'constants/notifierConstants';
import { registerNotifierCallback, unregisterNotifierCallback } from 'containers/notifier-stack/utils';

interface JobDetailsSyncProps {
  jobId: string;
  onJobNoteUpdated: VoidFn;
  onJobReload: VoidFn;
}

interface JobSSEEvent {
  type: string;
  data: unknown;
  time: string;
  job_id: string;
  merchant_id: string;
}

export const JobDetailsSync: FC<JobDetailsSyncProps> = ({ jobId, onJobNoteUpdated, onJobReload }) => {
  const dispatch = useAppDispatch();

  const { id: currentTeamId } = useAppSelector(selectCurrentTeam);
  const { id: userId } = useAppSelector(selectUserDetails);
  const notifications = useAppSelector(getNotifications);

  const { data: jobSSEData } = useSSE({
    // eslint-disable-next-line max-len
    url: `${API_BASE_URL.JOB_MANAGER}/${API_VERSION.V1}/events/${currentTeamId}/jobs/${jobId}?client_id=${currentTeamId}`
  });

  const NOTIFIER_RELOAD_JOB_KEY = `RELOAD_JOB_${jobId}`;

  useEffect(() => {
    registerNotifierCallback(NOTIFIER_RELOAD_JOB_KEY, onJobReload);
    return () => unregisterNotifierCallback(NOTIFIER_RELOAD_JOB_KEY);
  }, [onJobReload, jobId]);

  const handleShowNotifications = () => {
    const currentNotification = notifications.find(notification => notification.id === jobId);
    if (currentNotification) {
      return;
    }
    dispatch(
      showNotifier({
        message: {
          primaryMessage: 'Job details have been updated',
          actionMessage: 'Reload',
          actionKey: NOTIFIER_RELOAD_JOB_KEY
        },
        id: jobId,
        type: NotifierTypes.INFO,
        showClose: true,
        shouldAnimate: true,
        duration: 15000
      })
    );
  };

  useEffect(() => {
    if (jobSSEData) {
      const jobSSEEvent = jobSSEData as JobSSEEvent;
      if (userId === jobSSEEvent?.merchant_id) {
        return;
      }
      if (jobSSEEvent.type === JobSSEEvents.JOB_NOTE_UPDATED) {
        onJobNoteUpdated();
      } else if (jobSSEEvent.type === JobSSEEvents.JOB_UPDATED) {
        handleShowNotifications();
      }
    }
  }, [jobSSEData]);
  return <></>;
};
